// ======================================================================
// File: src/modules/ipe/dashboard/crm/components/TopCustomersWidget.jsx
// Purpose:
// CRM Executive Top Customers Widget
// ======================================================================

import React from "react";

export default function TopCustomersWidget({
  customers = [],
  onViewAll,
  onCustomerClick,
}) {
  const getNumericValue = (value) => {
    if (!value) return 0;

    const num = parseFloat(
      value
        .replace("₹", "")
        .replace("Cr", "")
        .replace("Lakhs", "")
        .trim()
    );

    return isNaN(num) ? 0 : num;
  };

  const totalRevenue = customers.reduce(
    (sum, c) => sum + getNumericValue(c.value),
    0
  );

  const topRevenue = Math.max(
    ...customers.map((c) =>
      getNumericValue(c.value)
    ),
    1
  );

  const getRankColor = (rank) => {
    switch (rank) {
      case 1:
        return "#f59e0b";
      case 2:
        return "#94a3b8";
      case 3:
        return "#b45309";
      default:
        return "#6366f1";
    }
  };

  return (
    <div
      style={{
        background: "#ffffff",
        borderRadius: "12px",
        padding: "18px",
        boxShadow:
          "0 2px 10px rgba(0,0,0,.06)",
        border: "1px solid #e5e7eb",
        height: "100%",
      }}
    >
      {/* =====================================================
          HEADER
      ===================================================== */}

      <div
        style={{
          display: "flex",
          justifyContent:
            "space-between",
          alignItems: "center",
          marginBottom: "18px",
        }}
      >
        <h3
          style={{
            margin: 0,
            fontSize: "18px",
            fontWeight: 700,
          }}
        >
          Top Customers
        </h3>

        <button
          onClick={onViewAll}
          style={{
            border:
              "1px solid #d1d5db",
            background: "#fff",
            padding: "6px 12px",
            borderRadius: "6px",
            cursor: "pointer",
            fontSize: "12px",
            fontWeight: 600,
          }}
        >
          View All
        </button>
      </div>

      {/* =====================================================
          SUMMARY
      ===================================================== */}

      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            "1fr 1fr",
          gap: "12px",
          marginBottom: "18px",
        }}
      >
        <div
          style={{
            background: "#eff6ff",
            padding: "12px",
            borderRadius: "10px",
          }}
        >
          <div
            style={{
              fontSize: "11px",
              color: "#64748b",
            }}
          >
            Total Revenue
          </div>

          <div
            style={{
              marginTop: "4px",
              fontSize: "22px",
              fontWeight: 700,
              color: "#2563eb",
            }}
          >
            ₹ {totalRevenue.toFixed(1)} Cr
          </div>
        </div>

        <div
          style={{
            background: "#ecfdf5",
            padding: "12px",
            borderRadius: "10px",
          }}
        >
          <div
            style={{
              fontSize: "11px",
              color: "#64748b",
            }}
          >
            Customers
          </div>

          <div
            style={{
              marginTop: "4px",
              fontSize: "22px",
              fontWeight: 700,
              color: "#16a34a",
            }}
          >
            {customers.length}
          </div>
        </div>
      </div>

      {/* =====================================================
          CUSTOMER LIST
      ===================================================== */}

      <div>
        {customers.map((customer, index) => {
          const numericValue =
            getNumericValue(customer.value);

          const contribution =
            (numericValue /
              totalRevenue) *
            100;

          const width =
            (numericValue /
              topRevenue) *
            100;

          return (
            <div
              key={customer.customer}
              onClick={() =>
                onCustomerClick?.(
                  customer
                )
              }
              style={{
                padding: "12px",
                marginBottom: "12px",
                border:
                  "1px solid #e5e7eb",
                borderRadius: "10px",
                cursor: "pointer",
                transition:
                  "all .25s ease",
                background: "#fff",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform =
                  "translateY(-2px)";
                e.currentTarget.style.boxShadow =
                  "0 8px 18px rgba(0,0,0,.08)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform =
                  "translateY(0)";
                e.currentTarget.style.boxShadow =
                  "none";
              }}
            >
              {/* TOP ROW */}

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                }}
              >
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "50%",
                    background:
                      getRankColor(
                        index + 1
                      ),
                    color: "#fff",
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    justifyContent:
                      "center",
                  }}
                >
                  #{index + 1}
                </div>

                <div
                  style={{
                    flex: 1,
                  }}
                >
                  <div
                    style={{
                      fontWeight: 700,
                      color: "#111827",
                    }}
                  >
                    {
                      customer.customer
                    }
                  </div>

                  <div
                    style={{
                      fontSize: "12px",
                      color: "#64748b",
                    }}
                  >
                    Revenue Contribution:
                    {" "}
                    {contribution.toFixed(
                      1
                    )}
                    %
                  </div>
                </div>

                <div
                  style={{
                    textAlign: "right",
                  }}
                >
                  <div
                    style={{
                      fontWeight: 700,
                      color: "#16a34a",
                    }}
                  >
                    {customer.value}
                  </div>
                </div>
              </div>

              {/* PROGRESS BAR */}

              <div
                style={{
                  marginTop: "10px",
                }}
              >
                <div
                  style={{
                    height: "8px",
                    background:
                      "#e5e7eb",
                    borderRadius:
                      "999px",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: `${width}%`,
                      height: "100%",
                      background:
                        "linear-gradient(90deg,#2563eb,#3b82f6)",
                    }}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* =====================================================
          FOOTER
      ===================================================== */}

      <div
        style={{
          borderTop:
            "1px solid #e5e7eb",
          marginTop: "12px",
          paddingTop: "12px",
          display: "flex",
          justifyContent:
            "space-between",
          fontSize: "12px",
          color: "#64748b",
        }}
      >
        <span>
          Highest Customer Revenue
        </span>

        <strong>
          {customers[0]?.value}
        </strong>
      </div>
    </div>
  );
}